import { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, BarChart, Bar, ReferenceLine } from 'recharts';

export default function CompensationCalculator() {
  const [monthlyAccounts, setMonthlyAccounts] = useState(4);
  const [monthlyMargin, setMonthlyMargin] = useState(100);
  const [currentSalary, setCurrentSalary] = useState(4000);
  const [baseAmount, setBaseAmount] = useState(2000);
  const [accountBonus, setAccountBonus] = useState(500);
  const [thresholdAmount, setThresholdAmount] = useState(10000);
  const [residualPercentage, setResidualPercentage] = useState(50);
  const [data, setData] = useState<any[]>([]);
  const [thresholdMonth, setThresholdMonth] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('graph');
  const [showMonthlyDetails, setShowMonthlyDetails] = useState(false);
  
  useEffect(() => {
    calculateProjections();
  }, [monthlyAccounts, monthlyMargin, currentSalary, baseAmount, accountBonus, thresholdAmount, residualPercentage]);
  
  const calculateProjections = () => {
    try {
      const projections:any[] = [];
      let totalAccounts = 0;
      let threshold = false;
      let thresholdReachedMonth = null;
      
      // Calculate for 60 months to ensure we see long-term effects
      for (let month = 1; month <= 60; month++) {
        // Add new accounts
        totalAccounts += Number(monthlyAccounts);
        
        // Calculate monthly residual
        const monthlyResidual = totalAccounts * Number(monthlyMargin);
        
        // Determine if threshold has been reached
        if (!threshold && monthlyResidual >= thresholdAmount) {
          threshold = true;
          thresholdReachedMonth = month;
        }
        
        // Calculate compensation components
        const baseSalary = threshold ? 0 : baseAmount;
        const accountBonusTotal = threshold ? 0 : (monthlyAccounts * accountBonus);
        const residualShare = threshold ? (monthlyResidual * (residualPercentage / 100)) : 0;
        
        // Total compensation
        const totalCompensation = baseSalary + accountBonusTotal + residualShare;
        
        // Calculate retention (assuming 98% monthly retention)
        const retentionFactor = 0.98; // 2% monthly churn
        
        projections.push({
          month,
          totalAccounts,
          monthlyResidual,
          baseSalary,
          accountBonusTotal,
          residualShare,
          compensation: totalCompensation,
          currentSalary: Number(currentSalary),
          difference: totalCompensation - Number(currentSalary),
          percentDifference: ((totalCompensation / Number(currentSalary) - 1) * 100).toFixed(1),
          cumulativeDifference: month === 1 ? 
            (totalCompensation - Number(currentSalary)) : 
            (projections[month-2].cumulativeDifference + (totalCompensation - Number(currentSalary))),
          retentionFactor
        });
      }
      
      setData(projections);
      setThresholdMonth(thresholdReachedMonth);
    } catch (error) {
      console.error("Error in calculation:", error);
    }
  };
  
  // Format currency
  const formatCurrency = (value:any) => {
    if (value === undefined || value === null) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  // Format percentage
  const formatPercentage = (value:number) => {
    if (value === undefined || value === null) return '0%';
    return `${Number(value).toFixed(1)}%`;
  };
  
  // Get color based on value (for difference highlighting)
  const getDifferenceColor = (value:number) => {
    if (value > 0) return 'text-green-600';
    if (value < 0) return 'text-red-600';
    return 'text-gray-600';
  };
  
  // Get the data for a specific timeline (first year, threshold, 3-year, 5-year)
  const getTimelineData = (timeline:string) => {
    if (!data || data.length === 0) return null;
    
    switch(timeline) {
      case 'year1':
        return data[11]; // Month 12
      case 'threshold':
        return thresholdMonth && thresholdMonth <= data.length ? data[thresholdMonth-1] : null;
      case 'year3':
        return data[35]; // Month 36
      case 'year5':
        return data[59]; // Month 60
      default:
        return null;
    }
  };
  
  // Calculate key metrics
  const year1Data = getTimelineData('year1');
  const thresholdData = getTimelineData('threshold');
  const year3Data = getTimelineData('year3');
  const year5Data = getTimelineData('year5');
  
  // Breakeven calculation
  const breakEvenAccounts = Math.ceil((currentSalary - baseAmount) / accountBonus);
  
  // Calculate time to ROI (when cumulative difference becomes positive)
  const roiMonth = data.findIndex(month => month.cumulativeDifference > 0) + 1;
  
  return (
    <div className="flex flex-col p-4 bg-white text-gray-800 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-4 text-center">Merchant Account Sales Compensation Calculator</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-bold text-lg mb-3">Sales Representative Inputs</h3>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Monthly Accounts Sold: {monthlyAccounts}
              </label>
              <div className="flex items-center">
                <span className="mr-2 text-sm">1</span>
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={monthlyAccounts}
                  onChange={(e) => setMonthlyAccounts(parseInt(e.target.value))}
                  className="flex-grow h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="ml-2 text-sm">20</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Monthly Margin per Account: ${monthlyMargin}
              </label>
              <div className="flex items-center">
                <span className="mr-2 text-sm">$100</span>
                <input
                  type="range"
                  min="100"
                  max="500"
                  step="25"
                  value={monthlyMargin}
                  onChange={(e) => setMonthlyMargin(parseInt(e.target.value))}
                  className="flex-grow h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="ml-2 text-sm">$500</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Current Monthly Salary: {formatCurrency(currentSalary)}
              </label>
              <div className="flex items-center">
                <span className="mr-2 text-sm">$2,000</span>
                <input
                  type="range"
                  min="2000"
                  max="10000"
                  step="500"
                  value={currentSalary}
                  onChange={(e) => setCurrentSalary(parseInt(e.target.value))}
                  className="flex-grow h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="ml-2 text-sm">$10,000</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-bold text-lg mb-3">Compensation Structure Inputs</h3>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Base Salary: {formatCurrency(baseAmount)}
              </label>
              <div className="flex items-center">
                <span className="mr-2 text-sm">$0</span>
                <input
                  type="range"
                  min="0"
                  max="5000"
                  step="500"
                  value={baseAmount}
                  onChange={(e) => setBaseAmount(parseInt(e.target.value))}
                  className="flex-grow h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="ml-2 text-sm">$5,000</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Per Account Bonus: {formatCurrency(accountBonus)}
              </label>
              <div className="flex items-center">
                <span className="mr-2 text-sm">$100</span>
                <input
                  type="range"
                  min="100"
                  max="1000"
                  step="50"
                  value={accountBonus}
                  onChange={(e) => setAccountBonus(parseInt(e.target.value))}
                  className="flex-grow h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="ml-2 text-sm">$1,000</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Threshold for Residuals: {formatCurrency(thresholdAmount)}
              </label>
              <div className="flex items-center">
                <span className="mr-2 text-sm">$5,000</span>
                <input
                  type="range"
                  min="5000"
                  max="20000"
                  step="1000"
                  value={thresholdAmount}
                  onChange={(e) => setThresholdAmount(parseInt(e.target.value))}
                  className="flex-grow h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="ml-2 text-sm">$20,000</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Residual Percentage: {residualPercentage}%
              </label>
              <div className="flex items-center">
                <span className="mr-2 text-sm">20%</span>
                <input
                  type="range"
                  min="20"
                  max="75"
                  step="5"
                  value={residualPercentage}
                  onChange={(e) => setResidualPercentage(parseInt(e.target.value))}
                  className="flex-grow h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="ml-2 text-sm">75%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white p-3 rounded-lg shadow border border-gray-200">
          <p className="text-sm text-gray-500">Months to Threshold</p>
          <p className="text-xl font-bold">
            {thresholdMonth ? thresholdMonth : 'Not reached in 5 years'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Time to reach {formatCurrency(thresholdAmount)} portfolio
          </p>
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow border border-gray-200">
          <p className="text-sm text-gray-500">Break-even Point</p>
          <p className="text-xl font-bold">
            {breakEvenAccounts} accounts/month
          </p>
          <p className="text-xs text-gray-500 mt-1">
            To match current {formatCurrency(currentSalary)} salary
          </p>
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow border border-gray-200">
          <p className="text-sm text-gray-500">Year 1 Monthly Income</p>
          <p className="text-xl font-bold">
            {year1Data ? formatCurrency(year1Data.compensation) : '$0'}
          </p>
          <p className={`text-xs ${year1Data && getDifferenceColor(year1Data.difference)} mt-1`}>
            {year1Data ? (year1Data.difference >= 0 ? '+' : '') + formatCurrency(year1Data.difference) : '$0'} vs. current
          </p>
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow border border-gray-200">
          <p className="text-sm text-gray-500">Time to Positive ROI</p>
          <p className="text-xl font-bold">
            {roiMonth > 0 ? `${roiMonth} months` : 'Never'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            When cumulative benefit exceeds losses
          </p>
        </div>
      </div>
      
      <div className="mb-4">
        <div className="flex border-b border-gray-200">
          <button
            className={`py-2 px-4 font-medium ${activeTab === 'graph' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('graph')}
          >
            Income Projection
          </button>
          <button
            className={`py-2 px-4 font-medium ${activeTab === 'portfolio' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('portfolio')}
          >
            Portfolio Growth
          </button>
          <button
            className={`py-2 px-4 font-medium ${activeTab === 'breakdown' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('breakdown')}
          >
            Income Breakdown
          </button>
          <button
            className={`py-2 px-4 font-medium ${activeTab === 'cumulative' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('cumulative')}
          >
            Cumulative Impact
          </button>
        </div>
      </div>
      
      <div className="h-80 w-full mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <>
          {activeTab === 'graph' && (
            <LineChart
              data={data.slice(0, 36)} // Show 3 years
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                label={{ value: 'Month', position: 'insideBottom', offset: -5 }} 
              />
              <YAxis 
                label={{ value: 'Monthly Income ($)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip 
                formatter={(value) => [formatCurrency(value), undefined]} 
                labelFormatter={(label) => `Month ${label}`}
              />
              <Legend />
              <ReferenceLine 
                y={currentSalary} 
                stroke="#FF8800" 
                strokeDasharray="3 3" 
                label={{ value: 'Current Salary', position: 'left' }} 
              />
              {thresholdMonth && thresholdMonth <= 36 && (
                <ReferenceLine 
                  x={thresholdMonth} 
                  stroke="#00AA00" 
                  strokeDasharray="3 3" 
                  label={{ value: 'Threshold Reached', position: 'top' }} 
                />
              )}
              <Line 
                type="monotone" 
                dataKey="currentSalary" 
                name="Current Salary" 
                stroke="#6b7280" 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="compensation" 
                name="New Compensation" 
                stroke="#3b82f6" 
                strokeWidth={2} 
                activeDot={{ r: 8 }}
              />
            </LineChart>
          )}
          {activeTab === 'portfolio' && (
            <AreaChart
              data={data.slice(0, 36)} // Show 3 years
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                label={{ value: 'Month', position: 'insideBottom', offset: -5 }} 
              />
              <YAxis 
                label={{ value: 'Portfolio Margin ($)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip 
                formatter={(value) => [formatCurrency(value), undefined]} 
                labelFormatter={(label) => `Month ${label}`}
              />
              <Legend />
              <ReferenceLine 
                y={thresholdAmount} 
                stroke="#FF8800" 
                strokeDasharray="3 3" 
                label={{ value: 'Threshold', position: 'right' }} 
              />
              <Area 
                type="monotone" 
                dataKey="monthlyResidual" 
                name="Portfolio Margin" 
                fill="#10b981" 
                stroke="#047857" 
                fillOpacity={0.3}
              />
              <Line 
                type="monotone" 
                dataKey="totalAccounts" 
                name="Total Accounts" 
                stroke="#6366f1" 
                yAxisId="right"
                hide={true} // Hidden by default as it uses a different scale
              />
            </AreaChart>
          )}
          {activeTab === 'breakdown' && (
            <BarChart
              data={data.slice(0, 36)} // Show 3 years
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                label={{ value: 'Month', position: 'insideBottom', offset: -5 }} 
              />
              <YAxis 
                label={{ value: 'Income Breakdown ($)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip 
                formatter={(value) => [formatCurrency(value), undefined]} 
                labelFormatter={(label) => `Month ${label}`}
              />
              <Legend />
              <ReferenceLine 
                y={currentSalary} 
                stroke="#FF8800" 
                strokeDasharray="3 3" 
                label={{ value: 'Current Salary', position: 'right' }} 
              />
              <Bar dataKey="baseSalary" name="Base Salary" stackId="a" fill="#6b7280" />
              <Bar dataKey="accountBonusTotal" name="Account Bonuses" stackId="a" fill="#3b82f6" />
              <Bar dataKey="residualShare" name="Residual Share" stackId="a" fill="#10b981" />
            </BarChart>
          )}
          {activeTab === 'cumulative' && (
            <AreaChart
              data={data.slice(0, 36)} // Show 3 years
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                label={{ value: 'Month', position: 'insideBottom', offset: -5 }} 
              />
              <YAxis 
                label={{ value: 'Cumulative Difference ($)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip 
                formatter={(value) => [formatCurrency(value), undefined]} 
                labelFormatter={(label) => `Month ${label}`}
              />
              <Legend />
              <ReferenceLine 
                y={0} 
                stroke="#000000" 
                strokeDasharray="3 3" 
              />
              <Area 
                type="monotone" 
                dataKey="cumulativeDifference" 
                name="Cumulative Difference" 
                stroke="#8884d8"
                fill="#8884d8"
                fillOpacity={0.3}
              />
            </AreaChart>
          )}</>
        </ResponsiveContainer>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="col-span-1 md:col-span-4">
          <h3 className="font-bold text-lg mb-2">Timeline Analysis</h3>
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow border border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <p className="font-medium">Year 1</p>
            <p className="text-sm text-gray-500">Month 12</p>
          </div>
          
          {year1Data && (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                <div>
                  <p className="text-gray-500">Portfolio:</p>
                  <p className="font-medium">{formatCurrency(year1Data.monthlyResidual)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Accounts:</p>
                  <p className="font-medium">{year1Data.totalAccounts}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-gray-500">Monthly Income:</p>
                  <p className="font-medium">{formatCurrency(year1Data.compensation)}</p>
                </div>
                <div>
                  <p className={`text-gray-500`}>vs Current:</p>
                  <p className={`font-medium ${getDifferenceColor(year1Data.difference)}`}>
                    {year1Data.difference >= 0 ? '+' : ''}{formatCurrency(year1Data.difference)}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow border border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <p className="font-medium">Threshold Point</p>
            <p className="text-sm text-gray-500">
              {thresholdData ? `Month ${thresholdMonth}` : 'Not reached'}
            </p>
          </div>
          
          {thresholdData ? (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                <div>
                  <p className="text-gray-500">Portfolio:</p>
                  <p className="font-medium">{formatCurrency(thresholdData.monthlyResidual)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Accounts:</p>
                  <p className="font-medium">{thresholdData.totalAccounts}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-gray-500">Monthly Income:</p>
                  <p className="font-medium">{formatCurrency(thresholdData.compensation)}</p>
                </div>
                <div>
                  <p className={`text-gray-500`}>vs Current:</p>
                  <p className={`font-medium ${getDifferenceColor(thresholdData.difference)}`}>
                    {thresholdData.difference >= 0 ? '+' : ''}{formatCurrency(thresholdData.difference)}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-amber-600">
              Portfolio doesn't reach {formatCurrency(thresholdAmount)} threshold within 5 years at current rate
            </p>
          )}
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow border border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <p className="font-medium">Year 3</p>
            <p className="text-sm text-gray-500">Month 36</p>
          </div>
          
          {year3Data && (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                <div>
                  <p className="text-gray-500">Portfolio:</p>
                  <p className="font-medium">{formatCurrency(year3Data.monthlyResidual)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Accounts:</p>
                  <p className="font-medium">{year3Data.totalAccounts}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-gray-500">Monthly Income:</p>
                  <p className="font-medium">{formatCurrency(year3Data.compensation)}</p>
                </div>
                <div>
                  <p className={`text-gray-500`}>vs Current:</p>
                  <p className={`font-medium ${getDifferenceColor(year3Data.difference)}`}>
                    {year3Data.difference >= 0 ? '+' : ''}{formatCurrency(year3Data.difference)}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow border border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <p className="font-medium">Year 5</p>
            <p className="text-sm text-gray-500">Month 60</p>
          </div>
          
          {year5Data && (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                <div>
                  <p className="text-gray-500">Portfolio:</p>
                  <p className="font-medium">{formatCurrency(year5Data.monthlyResidual)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Accounts:</p>
                  <p className="font-medium">{year5Data.totalAccounts}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-gray-500">Monthly Income:</p>
                  <p className="font-medium">{formatCurrency(year5Data.compensation)}</p>
                </div>
                <div>
                  <p className={`text-gray-500`}>vs Current:</p>
                  <p className={`font-medium ${getDifferenceColor(year5Data.difference)}`}>
                    {year5Data.difference >= 0 ? '+' : ''}{formatCurrency(year5Data.difference)}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-lg">Key Insights</h3>
          <button 
            className="text-sm text-blue-600 hover:text-blue-800"
            onClick={() => setShowMonthlyDetails(!showMonthlyDetails)}
          >
            {showMonthlyDetails ? 'Hide Monthly Details' : 'Show Monthly Details'}
          </button>
        </div>
        
        <ul className="list-disc pl-5 space-y-2 mb-4">
          <li>
            <strong>Monthly Performance:</strong> At {monthlyAccounts} accounts per month with ${monthlyMargin} margin per account, 
            {thresholdMonth ? 
              ` the ${formatCurrency(thresholdAmount)} portfolio threshold is reached in month ${thresholdMonth}.` : 
              ` the ${formatCurrency(thresholdAmount)} portfolio threshold is not reached within 5 years.`
            }
          </li>
          
          <li>
            <strong>Break-even Analysis:</strong> Need to sell {breakEvenAccounts} accounts per month to match current ${formatCurrency(currentSalary)} salary.
            {monthlyAccounts < breakEvenAccounts ? 
              ` This plan results in lower initial compensation than the current salary.` :
              monthlyAccounts === breakEvenAccounts ?
              ` This plan matches the current salary initially.` :
              ` This plan exceeds the current salary from day one.`
            }
          </li>
          
          {thresholdMonth && (
            <li>
              <strong>Threshold Impact:</strong> After reaching the {formatCurrency(thresholdAmount)} threshold in month {thresholdMonth}, 
              monthly income becomes {formatCurrency(thresholdData.compensation)}, 
              which is {thresholdData.difference >= 0 ? formatCurrency(thresholdData.difference) + ' more' : formatCurrency(Math.abs(thresholdData.difference)) + ' less'} than the current salary 
              ({formatPercentage(thresholdData.percentDifference)} {thresholdData.difference >= 0 ? 'increase' : 'decrease'}).
            </li>
          )}
          
          <li>
            <strong>Long-term Potential:</strong> By year 5, monthly income potential is {formatCurrency(year5Data?.compensation || 0)},
            representing a {formatPercentage(year5Data?.percentDifference || 0)} {(year5Data?.difference || 0) >= 0 ? 'increase' : 'decrease'} over current salary.
          </li>
          
          <li>
            <strong>ROI Timeline:</strong> {roiMonth > 0 ? 
              `Cumulative compensation benefit exceeds any initial reduction after ${roiMonth} months.` : 
              `This plan never produces a positive ROI compared to the current salary structure.`
            }
          </li>
        </ul>
        
        {showMonthlyDetails && (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-3 border-b text-left">Month</th>
                  <th className="py-2 px-3 border-b text-right">Accounts</th>
                  <th className="py-2 px-3 border-b text-right">Portfolio</th>
                  <th className="py-2 px-3 border-b text-right">Base</th>
                  <th className="py-2 px-3 border-b text-right">Bonuses</th>
                  <th className="py-2 px-3 border-b text-right">Residuals</th>
                  <th className="py-2 px-3 border-b text-right">Total</th>
                  <th className="py-2 px-3 border-b text-right">vs Current</th>
                </tr>
              </thead>
              <tbody>
                {data.slice(0, 24).map((month) => (
                  <tr key={month.month} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3">{month.month}</td>
                    <td className="py-2 px-3 text-right">{month.totalAccounts}</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(month.monthlyResidual)}</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(month.baseSalary)}</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(month.accountBonusTotal)}</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(month.residualShare)}</td>
                    <td className="py-2 px-3 text-right font-medium">{formatCurrency(month.compensation)}</td>
                    <td className={`py-2 px-3 text-right ${getDifferenceColor(month.difference)}`}>
                      {month.difference >= 0 ? '+' : ''}{formatCurrency(month.difference)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}